import { ReactElement } from 'react';
import { isEuCountryCode } from '../utility/countries';
import { formatGbp } from '../utility/money';

// Preview only — the charged rate is the card's stored treatment, resolved
// server-side. Outside UK+EU there is no UK VAT story to tell the customer at
// all, so the tax row is hidden rather than showing a confusing "VAT 0%".
export const previewTax = ({
  subtotal,
  country,
}: {
  subtotal: number;
  country: string | null | undefined;
}): {
  ratePercent: number;
  tax: number;
  note: string | null;
  showTaxRow: boolean;
} => {
  const code = country ?? '';

  if (code === '' || code === 'GB')
    return { ratePercent: 20, tax: subtotal * 0.2, note: null, showTaxRow: true };

  if (isEuCountryCode(code))
    return {
      ratePercent: 0,
      tax: 0,
      note: 'Reverse charge may apply with a valid EU VAT number.',
      showTaxRow: true,
    };

  return { ratePercent: 0, tax: 0, note: null, showTaxRow: false };
};

interface Props {
  subtotal: number;
  country: string | null | undefined;
  // Qualifier after "Now you pay", e.g. "(repeats every month)" or "(one-off)".
  totalLabel: string;
}

export const TaxSummary = ({
  subtotal,
  country,
  totalLabel,
}: Props): ReactElement => {
  const { ratePercent, tax, note, showTaxRow } = previewTax({
    subtotal,
    country,
  });

  return (
    <>
      {showTaxRow && (
        <div className="flex items-center justify-between text-body-medium">
          <span className="text-white">Taxes (VAT {ratePercent}%)</span>
          <span className="text-amber-400">{formatGbp(tax)}</span>
        </div>
      )}
      {note && (
        <p className="text-body-smallest text-text-low-priority">
          {note} Final tax is confirmed at checkout based on your card&apos;s
          issuing country.
        </p>
      )}
      <hr className="border-none h-px bg-grey-t-700" />
      <div className="flex items-center justify-between">
        <span className="font-semibold text-white">
          Now you pay{' '}
          <span className="text-text-low-priority font-normal">
            {totalLabel}
          </span>
        </span>
        <span className="font-semibold text-amber-400">
          {formatGbp(subtotal + tax)}
        </span>
      </div>
    </>
  );
};
